// src/pages/laporan/LaporanDetail.tsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Send, MessageSquare, Paperclip, Clock, User, Building2, Target, AlertTriangle, FileText, Download, TrendingUp, Tag, ArrowUpRight, BookOpen, BarChart3 } from 'lucide-react';
import { Button, Badge, Card, Breadcrumb, ProgressBar } from '../../components/ui';
import { StatusProgress, WorkflowActions, WorkflowTimeline } from '../../components/workflow';
import { LaporanMasalah, Comment } from '../../types';
import { formatDistanceToNow } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { laporanService } from '../../services/laporan';
import { useWorkflow } from '../../hooks/useWorkflow';
import { useRole } from '../../hooks/useRole';
import { useAuthStore } from '../../store/authStore';

const LaporanDetail: React.FC = () => {
  const { id } = useParams();
  const [laporan, setLaporan] = useState<any>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [activeTab, setActiveTab] = useState<'detail' | 'komentar' | 'riwayat' | 'lampiran'>('detail');

  const roleInfo = useRole();
  const currentUser = useAuthStore((s) => s.user);
  const workflow = useWorkflow(laporan);
  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await laporanService.getById(Number(id));
      const d = (data as any)?.data || data;
      setLaporan(d);
      setComments(d.comments || []);
      setAttachments(d.attachments || []);
    } catch (err) {
      console.error('Failed to load laporan:', err);
      toast.error('Gagal memuat detail laporan');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (id) fetchData();
  }, [id]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setSendingComment(true);
    try {
      await laporanService.addComment(Number(id), newComment);
      toast.success('Komentar berhasil ditambahkan');
      setNewComment('');
      const data = await laporanService.getById(Number(id));
      const d = (data as any)?.data || data;
      setComments(d.comments || []);
    } catch (err) { toast.error('Gagal mengirim komentar'); }
    finally { setSendingComment(false); }
  };

  const handleStatusChange = async (toState: string, notes?: string) => {
    const result = await workflow.changeStatus(toState, notes);
    if (result) {
      await fetchData(); // Refresh all data
    }
    return result;
  };

  if (loading) return (
    <div className="space-y-6 animate-in"><Breadcrumb items={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Laporan', href: '/laporan' }, { label: '...' }]} />
      <div className="flex items-center justify-center h-64"><div className="text-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-main mx-auto mb-3"></div><p className="text-sm text-neutral-text-secondary">Memuat detail laporan...</p></div></div></div>
  );

  if (!laporan) return (
    <div className="space-y-6"><Breadcrumb items={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Laporan', href: '/laporan' }, { label: 'Tidak Ditemukan' }]} />
      <Card><p className="text-center py-8 text-sm text-neutral-text-secondary">Laporan tidak ditemukan</p></Card></div>
  );

  const statusLaporan = laporan.status === 'draft' ? 'draf' : laporan.status;

  const sortedPpepp = Array.isArray(laporan.ppepp_rekomendasi)
    ? [...laporan.ppepp_rekomendasi].sort((a: any, b: any) => (b?.id || 0) - (a?.id || 0))
    : [];
  const statusPpeppDinonaktifkan = ['dibatalkan', 'batal', 'cancelled'];
  const ppeppAktifUntukSelesai = sortedPpepp.filter((item: any) => {
    const status = String(item?.status || '').toLowerCase();
    return !statusPpeppDinonaktifkan.includes(status);
  });
  const activePpepp = ppeppAktifUntukSelesai.find((item: any) => item?.status && item.status !== 'selesai')
    || ppeppAktifUntukSelesai[0]
    || null;
  const latestPpepp = sortedPpepp[0] || null;
  const latestPpeppId = activePpepp?.id || latestPpepp?.id || null;
  const totalPpeppAktif = ppeppAktifUntukSelesai.length;
  const semuaPpeppAktifMenungguKonfirmasi = totalPpeppAktif > 0
    && ppeppAktifUntukSelesai.every((item: any) =>
      Number(item?.progress_percentage || 0) >= 100 && String(item?.status || '') === 'selesai'
    );
  const bolehTampilTombolSelesai = statusLaporan === 'berlangsung' && semuaPpeppAktifMenungguKonfirmasi;
  const targetEfektif = activePpepp?.target_date || laporan.target_selesai;
  const sumberTarget = activePpepp?.target_date ? 'keputusan_rapim' : 'sla_awal';
  const isLaporanBerlangsung = statusLaporan === 'berlangsung';
  const ppeppForStatus = activePpepp || latestPpepp;
  const statusSelesaiKonteks = statusLaporan === 'selesai' || ppeppForStatus?.status === 'selesai';
  const tanggalSelesaiAktual = laporan.completed_at || laporan.updated_at || ppeppForStatus?.updated_at || null;
  const hariTerlambatHistoris = (() => {
    if (!statusSelesaiKonteks || !targetEfektif || !tanggalSelesaiAktual) return 0;
    const target = new Date(targetEfektif);
    const selesai = new Date(tanggalSelesaiAktual);
    target.setHours(0, 0, 0, 0);
    selesai.setHours(0, 0, 0, 0);
    if (selesai <= target) return 0;
    return Math.ceil((selesai.getTime() - target.getTime()) / (1000 * 60 * 60 * 24));
  })();
  const infoKetepatanSelesai = statusSelesaiKonteks
    ? (hariTerlambatHistoris > 0 ? `Selesai terlambat ${hariTerlambatHistoris} hari` : 'Selesai tepat waktu')
    : '';
  const ppeppProgress = Number(ppeppForStatus?.progress_percentage || 0);
  const isMenungguKonfirmasi = isLaporanBerlangsung
    && String(ppeppForStatus?.status || '') === 'selesai'
    && ppeppProgress >= 100
    && statusLaporan !== 'selesai';
  const isTerhambat = isLaporanBerlangsung && ppeppForStatus?.status === 'terhambat';
  const isMenungguPenjadwalanRapimUlang = statusLaporan === 'antrean_rapim' && Boolean(laporan.dikembalikan_ke_rapim);
  const isOverdue = (() => {
    if (!targetEfektif || statusSelesaiKonteks) return false;
    const targetDate = new Date(targetEfektif);
    const today = new Date();
    targetDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return targetDate < today;
  })();
  const filteredTransitions = workflow.transitions.filter((t) => {
    // Tombol validasi dan antrean rapim diizinkan muncul untuk role yang berwenang
    // 1. Tombol Selesai
    if (t.to_state === 'selesai' && !bolehTampilTombolSelesai) {
      return false;
    }

    // 2. Tombol Setujui L2 (to_state = 'validasi_l2') 
    // Hanya muncul jika status saat ini = 'validasi_l1'
    if (t.to_state === 'validasi_l2' && statusLaporan !== 'validasi_l1') {
      return false;
    }

    // 3. Tombol Masuk Antrean RAPIM (to_state = 'antrean_rapim')
    // Hanya muncul jika status saat ini = 'validasi_l1' atau 'validasi_l2' (fase validasi)
    if (t.to_state === 'antrean_rapim' && !['validasi_l1', 'validasi_l2'].includes(statusLaporan)) {
      return false;
    }

    // 4. Tombol Ditetapkan di RAPIM (to_state = 'diputuskan')
    // SELALU disembunyikan di halaman detail laporan — hanya dilakukan dari modul RAPIM
    if (t.to_state === 'diputuskan') {
      return false;
    }

    // 5. Tombol Jadwalkan RAPIM & Kembalikan ke Dekan — sembunyikan untuk admin dan rektor
    // Admin/Rektor menjadwalkan RAPIM melalui modul RAPIM, bukan dari halaman laporan.
    if ((roleInfo.isAdmin || roleInfo.isRektor) && ['antrean_rapim', 'validasi_l1'].includes(t.to_state) && statusLaporan === 'validasi_l2') {
      return false;
    }

    // 7. Tombol yang memerlukan status draft (edit-related)
    const draftOnlyTransitions = ['diajukan', 'submitted'];
    if (draftOnlyTransitions.includes(t.to_state) && !['draf', 'draft', 'ditolak_l1', 'ditolak_l2'].includes(statusLaporan)) {
      return false;
    }

    // 6. Tombol Submit/Ajukan - HANYA tampil jika status masih draft/draf
    if (t.to_state === 'diajukan' && !['draf', 'draft'].includes(statusLaporan)) {
      return false;
    }

    if (
      ['wakil_rektor_1', 'wakil_rektor_2', 'wakil_rektor_3', 'wakil_rektor_4'].includes(roleInfo.role)
      && statusLaporan === 'validasi_l2'
      && ['antrean_rapim', 'ditolak_l2'].includes(t.to_state)
    ) {
      return false;
    }

    return true;
  });

  // Logika badge alur laporan dinamis (Sesuai rujukan user: L1=Warek, L2=Antrian Rapat)
  const getAlurBadge = () => {
    const status = statusLaporan;
    const wt = laporan.warek_tujuan;

    if (isMenungguKonfirmasi) {
      return {
        label: 'Menunggu Konfirmasi Selesai - Progres 100%',
        color: '#F59E0B',
        prefix: 'Menunggu Konfirmasi',
      };
    }

    if (isTerhambat) {
      return {
        label: 'Tindak Lanjut Terhambat - Menunggu Keputusan',
        color: '#DC2626',
        prefix: 'Tindak Lanjut Terhambat',
      };
    }

    if (status === 'draf') {
      return pernahDitolakAtauRevisi
        ? {
          label: 'Perlu Revisi Pelapor',
          color: '#F59E0B',
          prefix: 'Revisi',
        }
        : {
          label: 'Draf - Belum Diajukan',
          color: '#6B7280',
          prefix: 'Draf',
        };
    }

    if (status === 'diajukan') {
      return {
        label: 'Menunggu Validasi: Dekan',
        color: '#6B7280',
        prefix: 'Menunggu Validasi',
      };
    }

    if (status === 'validasi_l1') {
      return {
        label: `${wt?.display_name ?? 'Wakil Rektor'}`,
        color: wt?.name?.includes('1') ? '#2563EB'
          : wt?.name?.includes('2') ? '#F97316'
            : wt?.name?.includes('3') ? '#16A34A'
              : wt?.name?.includes('4') ? '#9333EA' : '#6B7280',
        prefix: 'Menunggu Validasi',
      };
    }

    if (status === 'validasi_l2') {
      return {
        label: 'Telah Disetujui: Antrean RAPIM',
        color: '#1E293B',
        prefix: 'Telah Disetujui',
      };
    }

    if (status === 'antrean_rapim') {
      if (isMenungguPenjadwalanRapimUlang) {
        return {
          label: 'Menunggu Penjadwalan RAPIM Ulang',
          color: '#B45309',
          prefix: 'RAPIM Ulang',
        };
      }

      return {
        label: 'RAPIM Telah Dijadwalkan',
        color: '#1E293B',
        prefix: 'RAPIM',
      };
    }

    if (status === 'diputuskan') {
      return {
        label: 'Diputuskan di RAPIM - Tindak Lanjut Dimulai',
        color: '#16A34A',
        prefix: 'Diputuskan di RAPIM',
      };
    }

    if (status === 'berlangsung') {
      return {
        label: 'Tindak Lanjut Sedang Berjalan',
        color: '#16A34A',
        prefix: 'Tindak Lanjut',
      };
    }

    if (status === 'selesai') {
      return {
        label: 'Selesai',
        color: '#166534',
        prefix: 'Selesai',
      };
    }

    if (status === 'diarsipkan') {
      return {
        label: 'Laporan telah diarsipkan',
        color: '#475569',
        prefix: 'Arsip',
      };
    }

    return null;
  };

  const totalTransisi = Array.isArray(workflow.history) ? workflow.history.length : 0;
  const pernahDitolakAtauRevisi = Array.isArray(workflow.history) && workflow.history.some((item: any) =>
    ['ditolak_l1', 'ditolak_l2'].includes(item?.to_state)
    || (['draf', 'draft'].includes(item?.to_state) && ['diajukan', 'validasi_l1', 'validasi_l2'].includes(item?.from_state))
  );
  const alur = getAlurBadge();
  const statusBadge = (() => {
    if (isTerhambat) {
      return { type: 'custom' as const, label: 'Terhambat', className: 'bg-red-100 text-red-700' };
    }
    if (isMenungguKonfirmasi) {
      return { type: 'custom' as const, label: 'Menunggu Konfirmasi', className: 'bg-orange-100 text-orange-700' };
    }
    if (isLaporanBerlangsung) {
      return { type: 'status' as const, status: 'berlangsung' };
    }
    return { type: 'status' as const, status: statusLaporan };
  })();

  const canEditStatus = statusLaporan === 'draf' ? 'draf' : (laporan.status || '');
  const canEditLaporanFinal = roleInfo.canEditLaporan(canEditStatus as any)
    && (roleInfo.isAdmin || Number(laporan.user_id || laporan.user?.id || 0) === Number(currentUser?.id || 0));

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Laporan', href: '/laporan' }, { label: laporan.kode }]} />

      {/* ====== STATUS PROGRESS BAR ====== */}
      <Card className="!p-5">
        <StatusProgress currentStatus={statusLaporan} validasiL1Skipped={laporan.validasi_l1_skipped} />
      </Card>

      {laporan.dikembalikan_ke_rapim && (
        <Card className="border border-yellow-300 bg-yellow-50">
          <p className="text-sm text-yellow-800 font-semibold">
            Laporan ini dikembalikan ke RAPIM karena hambatan tindak lanjut - menunggu penjadwalan rapat baru.
          </p>
        </Card>
      )}

      {/* ====== HEADER + ACTIONS ====== */}
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <Link to="/laporan"><Button variant="ghost" size="sm" icon={<ArrowLeft className="w-4 h-4" />}>Kembali</Button></Link>
            <span className="text-sm font-mono text-teal-main bg-teal-main/5 px-2 py-1 rounded">{laporan.kode}</span>
          </div>
          <h1 className="text-xl font-black text-teal-dark tracking-tight">{laporan.judul}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-neutral-bg border border-neutral-border">
              <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-text-secondary">Status</span>
              {statusBadge.type === 'custom' ? (
                <span className={`inline-flex items-center font-semibold rounded-full px-2.5 py-0.5 text-xs ${statusBadge.className}`}>
                  {statusBadge.label}
                </span>
              ) : (
                <Badge variant="status" status={statusBadge.status} size="sm" />
              )}
            </div>
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-neutral-bg border border-neutral-border">
              <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-text-secondary">Risiko</span>
              <Badge variant="risk" risk={laporan.tingkat_risiko} size="sm" />
            </div>
            {isOverdue && (
              <div className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-danger/10 border border-danger/20">
                <AlertTriangle className="w-3.5 h-3.5 text-danger" />
                <span className="text-xs font-bold text-danger">Terlambat</span>
              </div>
            )}
          </div>
        </div>

        {/* ====== DYNAMIC ACTION BUTTONS (workflow-driven) ====== */}
        <div className="flex flex-col items-end gap-2">
          {canEditLaporanFinal && (
            <Link to={`/laporan/${laporan.id}/edit`}>
              <Button variant="outline" size="sm">Edit Laporan</Button>
            </Link>
          )}
          {/* Workflow transition buttons from API */}
          <WorkflowActions
            transitions={filteredTransitions}
            loading={workflow.loading}
            laporan={laporan}
            onChangeStatus={handleStatusChange}
          />
        </div>
      </div>

      {/* ====== CONTENT ====== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Tabs */}
          <div className="flex items-center gap-1 border-b border-neutral-border">
            {[
              { id: 'detail' as const, label: 'Detail', icon: <FileText className="w-4 h-4" /> },
              { id: 'komentar' as const, label: `Komentar (${comments.length})`, icon: <MessageSquare className="w-4 h-4" /> },
              { id: 'riwayat' as const, label: 'Riwayat Status', icon: <Clock className="w-4 h-4" /> },
              { id: 'lampiran' as const, label: `Lampiran (${attachments.length})`, icon: <Paperclip className="w-4 h-4" /> },
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-teal-main text-teal-main' : 'border-transparent text-neutral-text-secondary hover:text-neutral-text-primary'}`}>
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* Detail Tab */}
          {activeTab === 'detail' && (
            <Card>
              <div className="space-y-6">
                {/* KLASIFIKASI LAPORAN - Section baru */}
                <div className="p-5 bg-blue-50 rounded-xl border border-blue-200">
                  <h3 className="text-xs font-semibold uppercase tracking-widest text-blue-800 mb-4 flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    Klasifikasi Laporan
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">Kategori</p>
                      <div className="flex items-center gap-2 mt-1">
                        {laporan.kategori_masalah ? (
                          <>
                            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: laporan.kategori_masalah.color }} />
                            <span className="text-sm font-semibold text-neutral-text-primary">{laporan.kategori_masalah.nama}</span>
                          </>
                        ) : (
                          <span className="text-sm text-gray-400 italic">Tidak ditentukan</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">{alur?.prefix ?? 'Status'}</p>
                      <div className="mt-1">
                        {alur ? (
                          <div className="rounded-lg border border-blue-200 bg-white px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span
                                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold text-white"
                                style={{ backgroundColor: alur.color }}>
                                <ArrowUpRight className="w-3 h-3" />
                                {alur.prefix}
                              </span>
                              <span className="text-xs font-semibold text-neutral-text-primary">{alur.label}</span>
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm font-medium text-neutral-text-secondary italic">Belum ditentukan</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">Standar SPMI</p>
                      <p className="text-sm mt-1">
                        {laporan.standar_spmi ? (
                          <span className="font-medium text-neutral-text-primary">{laporan.standar_spmi.kode} - {laporan.standar_spmi.nama}</span>
                        ) : (
                          <span className="text-gray-400 italic">Tidak ditentukan</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">Indikator SPMI</p>
                      <p className="text-sm mt-1">
                        {laporan.indikator_spmi ? (
                          <span className="font-medium text-neutral-text-primary">{laporan.indikator_spmi.kode} - {laporan.indikator_spmi.nama}</span>
                        ) : (
                          <span className="text-gray-400 italic">Tidak ditentukan</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-2">Deskripsi Masalah</h3>
                  <p className="text-sm text-neutral-text-primary leading-relaxed whitespace-pre-wrap">{laporan.deskripsi}</p>
                </div>
                {laporan.dampak && (
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-2">Dampak</h3>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{laporan.dampak}</p>
                  </div>
                )}
                {laporan.akar_masalah && (
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-2">Akar Masalah</h3>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{laporan.akar_masalah}</p>
                  </div>
                )}
                {laporan.standar && (
                  <div className="p-4 bg-neutral-bg rounded-xl">
                    <h3 className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-2">Referensi SPMI</h3>
                    <p className="text-sm font-medium">{laporan.standar.kode} - {laporan.standar.nama}</p>
                    {laporan.indikator && <p className="text-xs text-neutral-text-secondary mt-1">Indikator: {laporan.indikator.kode} - {laporan.indikator.nama}</p>}
                  </div>
                )}

                {/* PPEPP Rekomendasi section - only visible after rapim_decided */}
                {laporan.ppepp_rekomendasi && laporan.ppepp_rekomendasi.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-3">Keputusan PPEPP</h3>
                    <div className="space-y-3">
                      {laporan.ppepp_rekomendasi.map((p: any) => {
                        const isPpeppAktif = p.id === latestPpeppId;
                        const picKonteksLabel = isPpeppAktif
                          ? 'PIC Aktif Saat Ini'
                          : (p.status === 'dibatalkan' ? 'PIC Pada Keputusan Ini (Historis)' : 'PIC Pada Keputusan Ini');
                        const picUserTampil = (!isPpeppAktif && p.status === 'dibatalkan' && p.pic_user_historis)
                          ? p.pic_user_historis
                          : p.pic_user;
                        const picUnitTampil = (!isPpeppAktif && p.status === 'dibatalkan' && p.pic_unit_historis)
                          ? p.pic_unit_historis
                          : p.pic_unit;
                        const adaPeralihanPic = p.status === 'dibatalkan'
                          && !!latestPpepp
                          && !!p.pic_user?.id
                          && !!latestPpepp?.pic_user?.id
                          && p.pic_user.id !== latestPpepp.pic_user.id;

                        return (
                          <div key={p.id} className="p-4 bg-neutral-bg rounded-xl border border-neutral-border">
                            <div className="flex items-center justify-between mb-4 pb-3 border-b border-neutral-border/50">
                              <div className="flex items-center gap-2">
                                {p.priority && (
                                  <Badge variant="risk" risk={p.priority === 'kritis' || p.priority === 'critical' ? 'kritis' : p.priority === 'high' || p.priority === 'tinggi' ? 'tinggi' : p.priority === 'medium' || p.priority === 'sedang' ? 'sedang' : 'rendah'} size="sm" />
                                )}
                                <span className="text-xs text-neutral-text-secondary">
                                  Target: {p.target_date ? new Date(p.target_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
                                </span>
                              </div>
                              <Badge variant="status" status={p.status} size="sm" />
                            </div>

                            <div className="grid grid-cols-1 gap-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1">
                                  <p className="text-[10px] font-bold text-neutral-text-secondary uppercase">Program</p>
                                  <p className="text-sm font-medium text-neutral-text-primary bg-white/50 p-2 rounded border border-neutral-border/50">{p.program}</p>
                                </div>
                                <div className="space-y-1">
                                  <p className="text-[10px] font-bold text-neutral-text-secondary uppercase">Pengendalian</p>
                                  <p className="text-sm font-medium text-neutral-text-primary bg-white/50 p-2 rounded border border-neutral-border/50">{p.pengendalian}</p>
                                </div>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-1">
                                  <p className="text-[10px] font-bold text-neutral-text-secondary uppercase">Evaluasi</p>
                                  <p className="text-sm text-neutral-text-primary">{p.evaluasi}</p>
                                </div>
                                <div className="space-y-1">
                                  <p className="text-[10px] font-bold text-neutral-text-secondary uppercase">Pengembangan</p>
                                  <p className="text-sm text-neutral-text-primary">{p.pengembangan}</p>
                                </div>
                                <div className="space-y-1">
                                  <p className="text-[10px] font-bold text-neutral-text-secondary uppercase">Pelaporan</p>
                                  <p className="text-sm text-neutral-text-primary">{p.pelaporan}</p>
                                </div>
                              </div>

                              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-neutral-border/50">
                                <User className="w-3.5 h-3.5 text-neutral-text-secondary" />
                                <span className="text-xs text-neutral-text-secondary">{picKonteksLabel}:</span>
                                <span className="text-xs font-semibold text-teal-main">{picUserTampil?.name || '-'}</span>
                                <span className="text-xs text-neutral-text-secondary">Unit:</span>
                                <span className="text-xs font-semibold text-teal-main">{picUnitTampil?.name || '-'}</span>
                              </div>

                              {adaPeralihanPic && (
                                <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-2.5 py-1.5">
                                  Tanggung jawab telah dialihkan ke <strong>{latestPpepp.pic_user?.name || '-'}</strong> sebagai PIC aktif.
                                </div>
                              )}
                              {isPpeppAktif && (
                                <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1.5">
                                  Ini adalah keputusan PPEPP yang aktif saat ini.
                                </div>
                              )}
                              {!isPpeppAktif && (
                                <div className="text-xs text-neutral-text-secondary bg-white/70 border border-neutral-border rounded-md px-2.5 py-1.5">
                                  Ini adalah riwayat keputusan PPEPP sebelumnya.
                                </div>
                              )}
                            </div>

                            <div className="mt-4 flex items-center justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex justify-between text-xs mb-1">
                                  <span className="font-semibold text-neutral-text-secondary">Progress Tindak Lanjut</span>
                                  <span className="font-bold text-teal-main">{p.progress_percentage || 0}%</span>
                                </div>
                                <ProgressBar value={p.progress_percentage || 0} color="teal" />
                              </div>
                              <Link to={`/tindak-lanjut/${p.id}`}>
                                <Button variant="outline" size="sm" icon={<ArrowUpRight className="w-3.5 h-3.5" />}>
                                  Lihat Tindak Lanjut
                                </Button>
                              </Link>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Comments Tab */}
          {activeTab === 'komentar' && (
            <Card>
              <div className="space-y-4">
                {comments.map((c: any) => (
                  <div key={c.id} className={`flex gap-3 ${c.parent_id ? 'ml-10' : ''}`}>
                    <div className="w-8 h-8 rounded-full bg-teal-main text-white flex items-center justify-center text-xs font-bold shrink-0">
                      {c.user?.name?.charAt(0) || 'U'}
                    </div>
                    <div className="flex-1 bg-neutral-bg rounded-xl p-4">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold">{c.user?.name}</span>
                          <span className="text-xs text-neutral-text-secondary">{c.user?.role?.display_name || ''}</span>
                        </div>
                        <span className="text-xs text-neutral-text-secondary">
                          {c.created_at ? formatDistanceToNow(new Date(c.created_at), { addSuffix: true, locale: idLocale }) : ''}
                        </span>
                      </div>
                      <p className="text-sm leading-relaxed">{c.content}</p>
                    </div>
                  </div>
                ))}
                <div className="flex gap-3 pt-4 border-t border-neutral-border">
                  <div className="w-8 h-8 rounded-full bg-teal-main text-white flex items-center justify-center text-xs font-bold shrink-0">U</div>
                  <div className="flex-1">
                    <textarea value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Tulis komentar..."
                      className="w-full rounded-xl border border-neutral-border px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-light" rows={3} />
                    <div className="flex justify-end mt-2">
                      <Button variant="primary" size="sm" onClick={handleAddComment} loading={sendingComment} icon={<Send className="w-4 h-4" />}>Kirim</Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* History Tab - NOW shows state transitions from StateMachine */}
          {activeTab === 'riwayat' && (
            <Card>
              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-xl border border-neutral-border bg-neutral-bg px-4 py-3">
                  <div>
                    <p className="text-sm font-bold text-neutral-text-primary">Riwayat Alur Laporan</p>
                    <p className="text-xs text-neutral-text-secondary">Urutan perubahan status dari awal sampai kondisi saat ini.</p>
                  </div>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white border border-neutral-border text-neutral-text-secondary">
                    {workflow.historyLoading ? 'Memuat...' : `${totalTransisi} transisi`}
                  </span>
                </div>
                <WorkflowTimeline
                  history={workflow.history}
                  loading={workflow.historyLoading}
                />
              </div>
            </Card>
          )}

          {/* Attachments Tab */}
          {activeTab === 'lampiran' && (
            <Card>
              <div className="space-y-3">
                {attachments.length > 0 ? attachments.map((file: any, i: number) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-neutral-bg rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="w-10 h-10 bg-teal-main/10 rounded flex items-center justify-center"><FileText className="w-5 h-5 text-teal-main" /></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{file.original_name || file.filename}</p>
                      <p className="text-xs text-neutral-text-secondary">{file.size ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : ''}</p>
                    </div>
                    <Button variant="ghost" size="sm" icon={<Download className="w-4 h-4" />} onClick={() => laporanService.downloadAttachment(Number(id), file.id, file.original_name || file.filename)}>Unduh</Button>
                  </div>
                )) : <p className="text-sm text-neutral-text-secondary text-center py-4">Tidak ada lampiran</p>}
              </div>
            </Card>
          )}
        </div>

        {/* ====== SIDEBAR ====== */}
        <div className="space-y-4">
          {/* Status Laporan */}
          <Card borderColor="border-l-teal-main">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-3">Status Laporan</h3>
            <div className="flex items-center gap-2 flex-wrap">
              {isMenungguPenjadwalanRapimUlang ? (
                <span className="inline-flex items-center font-semibold rounded-full bg-amber-100 text-amber-700 px-3 py-1 text-sm">
                  Menunggu RAPIM Ulang
                </span>
              ) : (
                <Badge variant="status" status={statusLaporan} size="md" />
              )}
              {isOverdue && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-danger/10 text-danger">
                  <AlertTriangle className="w-3 h-3" /> Terlambat
                </span>
              )}
            </div>
            <div className="mt-3 text-xs text-neutral-text-secondary">
              {statusLaporan === 'draf' && (pernahDitolakAtauRevisi
                ? 'Laporan perlu revisi dan diajukan ulang pelapor'
                : 'Laporan masih draf dan belum diajukan')}
              {statusLaporan === 'diajukan' && 'Menunggu validasi Dekan/Kalem'}
              {statusLaporan === 'validasi_l1' && 'Menunggu validasi Wakil Rektor'}
              {statusLaporan === 'validasi_l2' && 'Menunggu diagendakan RAPIM'}
              {statusLaporan === 'antrean_rapim' && (isMenungguPenjadwalanRapimUlang
                ? 'Menunggu penjadwalan RAPIM ulang'
                : 'Dalam antrian agenda RAPIM')}
              {statusLaporan === 'diputuskan' && 'Keputusan PPEPP siap dijalankan'}
              {statusLaporan === 'berlangsung' && 'Pelaksanaan tindak lanjut berlangsung'}
              {statusLaporan === 'selesai' && 'Semua tindak lanjut telah selesai'}
              {statusLaporan === 'diarsipkan' && 'Laporan telah diarsipkan'}
              {statusLaporan === 'ditolak_l1' && 'Perlu revisi dari pelapor'}
              {statusLaporan === 'ditolak_l2' && 'Perlu revisi dari pelapor'}
            </div>
          </Card>

          {/* Status PIC - Jembatan Mikro */}
          {activePpepp && isLaporanBerlangsung && (
            <Card borderColor="border-l-info">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-3">Progres Teknis PIC</h3>
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-medium text-neutral-text-primary">Tindak Lanjut</span>
                <span className="text-sm font-bold text-teal-main">{activePpepp.progress_percentage || 0}%</span>
              </div>
              <ProgressBar value={activePpepp.progress_percentage || 0} color="teal" />
              <div className="mt-3 text-[11px] leading-relaxed">
                {isMenungguKonfirmasi ? (
                  <span className="text-amber-700 font-medium">PIC telah mengajukan konfirmasi selesai. Menunggu persetujuan pimpinan.</span>
                ) : isTerhambat ? (
                  <span className="text-red-700 font-medium">PIC melaporkan hambatan. Proses pengerjaan terhenti sementara.</span>
                ) : (
                  <span className="text-teal-700 font-medium">PIC sedang mengerjakan tindak lanjut secara teknis.</span>
                )}
              </div>
              <Link to={`/tindak-lanjut/${activePpepp.id}`} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-teal-main hover:underline">
                Buka Riwayat Progres <ArrowUpRight className="w-3 h-3" />
              </Link>
            </Card>
          )}

          {/* Informasi Pelapor & Unit */}
          <Card>
            <h3 className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-4">Informasi</h3>
            <div className="space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <User className="w-4 h-4 text-neutral-text-secondary mt-0.5" />
                <div>
                  <p className="text-xs text-neutral-text-secondary">Pelapor</p>
                  <p className="font-medium">{laporan.user?.name}</p>
                  <p className="text-xs text-neutral-text-secondary">{laporan.user?.role?.display_name || ''}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Building2 className="w-4 h-4 text-neutral-text-secondary mt-0.5" />
                <div>
                  <p className="text-xs text-neutral-text-secondary">Unit</p>
                  <p className="font-medium">{laporan.unit?.name || '-'}</p>
                </div>
              </div>

              {/* Target Selesai - with overdue indicator */}
              <div className="flex items-start gap-3">
                <Target className={`w-4 h-4 mt-0.5 ${isOverdue ? 'text-danger' : 'text-neutral-text-secondary'}`} />
                <div>
                  <p className="text-xs text-neutral-text-secondary">{statusSelesaiKonteks ? 'Tanggal Selesai Aktual' : 'Target Selesai'}</p>
                  <p className={`font-medium ${isOverdue ? 'text-danger' : ''}`}>
                    {statusSelesaiKonteks
                      ? (tanggalSelesaiAktual ? new Date(tanggalSelesaiAktual).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-')
                      : (targetEfektif ? new Date(targetEfektif).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-')}
                  </p>
                  <p className={`text-[10px] mt-0.5 ${isOverdue ? 'text-danger font-semibold' : statusSelesaiKonteks ? 'text-neutral-text-secondary' : (sumberTarget === 'keputusan_rapim' ? 'text-blue-600' : 'text-neutral-text-secondary')}`}>
                    {isOverdue
                      ? '⚠ Target sudah terlewat'
                      : statusSelesaiKonteks
                        ? infoKetepatanSelesai
                        : (sumberTarget === 'keputusan_rapim' ? 'ditetapkan RAPIM' : 'SLA awal')}
                  </p>
                </div>
              </div>

              {/* Dibuat */}
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-neutral-text-secondary mt-0.5" />
                <div>
                  <p className="text-xs text-neutral-text-secondary">Dibuat</p>
                  <p className="font-medium">{laporan.created_at ? formatDistanceToNow(new Date(laporan.created_at), { addSuffix: true, locale: idLocale }) : '-'}</p>
                </div>
              </div>
            </div>
          </Card>

          {/* PIC Aktif - only when tindak lanjut assigned */}
          {activePpepp && activePpepp.pic_user && (
            <Card borderColor="border-l-info">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-3">PIC Tindak Lanjut</h3>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-teal-main text-white flex items-center justify-center text-sm font-bold shrink-0">
                  {activePpepp.pic_user.name?.charAt(0) || 'P'}
                </div>
                <div>
                  <p className="text-sm font-semibold">{activePpepp.pic_user.name}</p>
                  <p className="text-xs text-neutral-text-secondary">{activePpepp.pic_unit?.name || '-'}</p>
                </div>
              </div>
              {activePpepp.id && (
                <Link to={`/tindak-lanjut/${activePpepp.id}`} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-teal-main hover:underline">
                  Lihat Detail Tindak Lanjut <ArrowUpRight className="w-3 h-3" />
                </Link>
              )}
            </Card>
          )}

          {/* Skor Kualitas */}
          <Card>
            <h3 className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-3">Skor Kualitas</h3>
            <ProgressBar
              value={laporan.completeness_percentage || 0}
              color="teal"
              label="Kelengkapan"
            />
            <p className="text-xs text-neutral-text-secondary mt-2">
              Kelengkapan: {laporan.completeness_percentage || 0}% (judul, deskripsi, dampak, akar masalah, standar, indikator, lampiran)
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LaporanDetail;
